import { EEthereumNetworkId } from '@ankr.com/provider';
import BigNumber from 'bignumber.js';
import config from 'config';
import { TransactionReceipt } from 'web3-core';

import { ErrorMessage, isEstimateFee, ZERO } from 'modules/common';
import {
  ContractsManager,
  convertToWei,
  getRegisterAbiItem,
  isWeb3KeyWriteProvider,
} from 'modules/core';
import { api, cacheTags } from 'modules/store';

export interface CreateCompatibleTaskParams {
  accountAddress: string;
  taskName: string;
  chainId: EEthereumNetworkId | null;
  taskContractAddress: string;
  taskBalance?: BigNumber;
  gasLimit: BigNumber;
}

export const { useLazyCreateCompatibleTaskQuery } = api.injectEndpoints({
  endpoints: build => ({
    createCompatibleTask: build.query<
      TransactionReceipt,
      CreateCompatibleTaskParams
    >({
      queryFn: async ({
        chainId,
        accountAddress,
        taskName,
        taskContractAddress,
        taskBalance = ZERO,
        gasLimit,
      }) => {
        if (!chainId) {
          throw new Error(ErrorMessage.ChainIdIsNotDefined);
        }

        const networkConfig = config[chainId];
        if (!networkConfig) {
          throw new Error(ErrorMessage.ConfigIsNotDefined);
        }

        const sdk = await ContractsManager.getInstance();
        const provider = await sdk.getProvider();
        if (!isWeb3KeyWriteProvider(provider)) {
          throw new Error(ErrorMessage.ProviderIsNotValid);
        }
        const web3 = provider.getWeb3();
        const registrarContract = await sdk.getRegistrarContract();
        const registerAbi = getRegisterAbiItem();

        const encodedRegisterCall = web3.eth.abi.encodeFunctionCall(
          registerAbi,
          [
            taskName,
            '0x1234', // encoded email
            taskContractAddress,
            gasLimit.toString(),
            accountAddress,
            '0x', // check data
            convertToWei(taskBalance),
            String(chainId), // source ( chain id ? )
            accountAddress,
            String(Math.round(Date.now() / 1000)), // unix timestamp in seconds
          ],
        );

        const data = registrarContract.methods
          .transferWithData(encodedRegisterCall)
          .encodeABI();

        const { contracts } = networkConfig;

        const info = await sdk.getGasInfo({
          contract: registrarContract,
          method: 'transferWithData',
          args: [encodedRegisterCall],
          value: taskBalance,
          currentAccount: accountAddress,
        });

        const estimateFee = isEstimateFee(chainId);
        const { receiptPromise } = await provider.sendTransactionAsync(
          accountAddress,
          contracts.registrar,
          {
            data,
            estimateFee,
            gasLimit: info.gasLimit.toString(),
            value: convertToWei(taskBalance),
          },
        );

        return { data: await receiptPromise };
      },
      providesTags: [cacheTags.taskData],
    }),
  }),
});
